import { t } from '@lingui/core/macro'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ky from 'ky'

import { getDisplayMessage } from '../../../components/api/displayMessage'
import {
  RequestError,
  extractHttpError,
} from '../../../components/api/extractHttpError'
import { API_PREFIX } from '../../../config'
import { logEvents } from '../../logEvents'
import { useMutateFeed } from '../../useFeed'
import { getFriendlyError } from './errorMessages'

export type ChatLink = {
  id: number
  title: string
  url: string
  score: number
  language: string
  puzzelKbId: string | null
}

export type ChatAnswer = {
  queryId: string
  question: string
  date: string
  reply: string | null
  links: ChatLink[]
  errorCode: string | null
  errorMessage: string | null
}

export type ChatResponse = {
  uid: string
  organizationUid: string
  created: string
  answers: ChatAnswer[]
}

export type AskQuestionPayload = {
  question: string
  uid: string | null
}

const getSkeletonAnswer = (question: string) => {
  return {
    queryId: crypto.randomUUID(),
    question,
    date: new Date().toISOString(),
    reply: null,
    links: [],
    errorCode: null,
    errorMessage: null,
  }
}

const getSkeletonChat = (
  question: string,
  preferredUid?: string | null,
): ChatResponse => {
  const tempChatUid = preferredUid || crypto.randomUUID()
  const skeletonAnswer = getSkeletonAnswer(question)
  return {
    uid: tempChatUid,
    organizationUid: '',
    created: new Date().toISOString(),
    answers: [skeletonAnswer],
  }
}

export const useAskQuestion = ({
  copilotToken,
  sessionUid,
}: {
  sessionUid: string
  copilotToken: string | null
}) => {
  const queryClient = useQueryClient()
  const { add, update } = useMutateFeed(sessionUid)

  const mutation = useMutation<
    ChatResponse,
    RequestError,
    AskQuestionPayload & { retryFeedItemUid?: string },
    {
      feedItemUid: string
    }
  >({
    mutationKey: ['ask-question'],
    retry: false,
    mutationFn: async (payload) => {
      try {
        const response = await ky
          .post(`${API_PREFIX}/co-pilot/v1/chats/ask?token=${copilotToken}`, {
            json: {
              question: payload.question,
              uid: payload.uid,
            },
            timeout: 120_000, // 2 minutes
          })
          .json<ChatResponse>()

        return response
      } catch (error) {
        const e = await extractHttpError(error)
        logEvents({
          eventType: 'useAskQuestion error',
          level: 'error',
          name: e.name,
          message: e.message,
          ...e.cause,
        })

        // The feed renders the error's name and message, so both have to be
        // copy we are happy to show an agent. The technical detail stays in
        // `cause` for telemetry.
        const presentableError = new RequestError(getDisplayMessage(e), e.cause)
        presentableError.name = t`Failed to send message`

        throw presentableError
      }
    },
    onMutate: (variables) => {
      const originalPayload = {
        question: variables.question,
        uid: variables.uid,
      }
      // If retrying, use the existing feed item
      if (variables.retryFeedItemUid) {
        update(variables.retryFeedItemUid, {
          type: 'ask-copilot',
          status: 'pending',
          data: {
            chat: getSkeletonChat(variables.question, variables.uid),
            originalPayload,
          },
        })
        return { feedItemUid: variables.retryFeedItemUid }
      }

      // For new questions, create a new item
      const feedItemUid = add({
        type: 'ask-copilot',
        status: 'pending',
        data: {
          chat: getSkeletonChat(variables.question, variables.uid),
          originalPayload,
        },
      })

      return { feedItemUid }
    },
    onError: (error, variables, context) => {
      if (!context?.feedItemUid) return

      update(context.feedItemUid, {
        type: 'ask-copilot',
        status: 'error',
        data: {
          question: variables.question,
          error,
          originalPayload: {
            question: variables.question,
            uid: variables.uid,
          },
        },
      })
    },
    onSuccess: (data, _variables, context) => {
      if (!context?.feedItemUid) return

      queryClient.setQueryData([copilotToken, 'chat', data.uid], data)

      const latestAnswer = data.answers.at(-1)
      if (!latestAnswer) {
        return
      }

      // If backend responded 200 OK but included an errorCode in the answer,
      // convert it into a user-friendly AskCopilotError feed item with retry.
      if (latestAnswer.errorCode) {
        const { title, message } = getFriendlyError(
          latestAnswer.errorCode,
          latestAnswer.errorMessage,
        )

        const err = new RequestError(message, {
          errorCode: latestAnswer.errorCode,
          serverMessage: latestAnswer.errorMessage,
          chatUid: data.uid,
        })
        // Show friendly title in the error alert
        err.name = title

        update(context.feedItemUid, {
          type: 'ask-copilot',
          status: 'error',
          data: {
            question: latestAnswer.question,
            error: err,
            originalPayload: {
              question: latestAnswer.question,
              uid: data.uid,
            },
          },
        })
        return
      }

      update(context.feedItemUid, {
        type: 'ask-copilot',
        status: 'complete',
        data: {
          chat: {
            ...data,
            answers: [latestAnswer],
          },
        },
      })
    },
  })

  return mutation
}
